import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ArrowRightIcon, XIcon, MenuIcon } from 'lucide-react';
interface WebsiteLayoutProps {
  children: React.ReactNode;
}
export function WebsiteLayout({
  children
}: WebsiteLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const navigation = [{
    name: 'Platform',
    href: '/website/platform'
  }, {
    name: 'Products',
    items: [{
      name: 'Copilot',
      href: '/copilot',
      description: 'AI-guided prospecting and outreach'
    }, {
      name: 'GTM Studio',
      href: '/products/gtm-studio',
      description: 'Visual play builder and segmentation'
    }, {
      name: 'Orchestration',
      href: '/orchestration',
      description: 'Multi-system GTM workflows'
    }, {
      name: 'Data & Signals',
      href: '/data-and-signals',
      description: 'Unified GTM data model'
    }, {
      name: 'Predictive',
      href: '/predictive',
      description: 'AI-powered account scoring'
    }]
  }, {
    name: 'GTM Library',
    items: [{
      name: 'Plays',
      href: '/gtm/plays',
      description: 'Signal-based GTM actions you can run now'
    }, {
      name: 'Playbooks',
      href: '/gtm/playbooks',
      description: 'Curated sequences for pipeline, expansion, and win-back'
    }]
  }, {
    name: 'Pricing',
    href: '/pricing'
  }];
  return <div className="w-full min-h-screen bg-white" data-id="element-8009">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200" data-id="element-8010">
        <div className="max-w-7xl mx-auto px-6" data-id="element-8011">
          <div className="flex items-center justify-between h-16" data-id="element-8012">
            {/* Logo */}
            <Link to="/website" className="flex items-center gap-3" data-id="element-8013">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center text-white font-bold text-lg" data-id="element-8014">
                Z
              </div>
              <div className="hidden sm:block" data-id="element-8015">
                <div className="text-lg font-bold text-gray-900" data-id="element-8016">ZoomInfo</div>
                <div className="text-xs text-gray-500" data-id="element-8017">GTM Platform</div>
              </div>
            </Link>
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-id="element-8018">
              {navigation.map(item => item.items ? <div key={item.name} className="relative group" data-id="element-8019">
                    <button className="text-gray-700 hover:text-gray-900 font-medium flex items-center gap-1" data-id="element-8020">
                      {item.name}
                      {item.badge && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded" data-id="element-8021">
                          {item.badge}
                        </span>}
                      <ChevronDownIcon className="w-4 h-4" data-id="element-8022" />
                    </button>
                    <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all" data-id="element-8023">
                      <div className="py-2" data-id="element-8024">
                        {item.items.map(subItem => <Link key={subItem.href} to={subItem.href} className="block px-4 py-3 hover:bg-gray-50" data-id="element-8025">
                            <div className="flex items-center justify-between" data-id="element-8026">
                              <div className="font-medium text-gray-900" data-id="element-8027">
                                {subItem.name}
                              </div>
                              {subItem.badge && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded" data-id="element-8028">
                                  {subItem.badge}
                                </span>}
                            </div>
                            {subItem.description && <p className="text-sm text-gray-600 mt-1" data-id="element-8029">
                                {subItem.description}
                              </p>}
                          </Link>)}
                      </div>
                    </div>
                  </div> : <Link key={item.name} to={item.href!} className="text-gray-700 hover:text-gray-900 font-medium" data-id="element-8030">
                    {item.name}
                  </Link>)}
            </nav>
            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3" data-id="element-8031">
              <Link to="/login" className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium" data-id="element-8032">
                Sign In
              </Link>
              <Link to="/signup" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium" data-id="element-8033">
                Get Started Free
              </Link>
            </div>
            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-gray-700 hover:text-gray-900" data-id="element-8034">
              {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-8035" /> : <MenuIcon className="w-6 h-6" data-id="element-8036" />}
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        {isMobileMenuOpen && <div className="lg:hidden border-t border-gray-200 bg-white" data-id="element-8037">
            <div className="max-w-7xl mx-auto px-6 py-4 space-y-4" data-id="element-8038">
              {navigation.map(item => item.items ? <div key={item.name} data-id="element-8039">
                    <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2" data-id="element-8040">
                      {item.name}
                      {item.badge && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded" data-id="element-8041">
                          {item.badge}
                        </span>}
                    </div>
                    <div className="space-y-2 pl-4" data-id="element-8042">
                      {item.items.map(subItem => <Link key={subItem.href} to={subItem.href} onClick={() => setIsMobileMenuOpen(false)} className="block" data-id="element-8043">
                          <div className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-8044">
                            {subItem.name}
                            {subItem.badge && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded" data-id="element-8045">
                                {subItem.badge}
                              </span>}
                          </div>
                          {subItem.description && <p className="text-xs text-gray-500 mt-1" data-id="element-8046">
                              {subItem.description}
                            </p>}
                        </Link>)}
                    </div>
                  </div> : <Link key={item.name} to={item.href!} onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 hover:text-gray-900 font-medium" data-id="element-8047">
                    {item.name}
                  </Link>)}
              <div className="pt-4 border-t border-gray-200 space-y-2" data-id="element-8048">
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="block w-full px-4 py-2 text-center text-gray-700 hover:text-gray-900 font-medium border border-gray-300 rounded-lg" data-id="element-8049">
                  Sign In
                </Link>
                <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)} className="block w-full px-4 py-2 text-center bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium" data-id="element-8050">
                  Get Started Free
                </Link>
              </div>
            </div>
          </div>}
      </header>
      {/* Main Content */}
      <main data-id="element-8051">{children}</main>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12" data-id="element-8052">
        <div className="max-w-7xl mx-auto px-6" data-id="element-8053">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8" data-id="element-8054">
            <div data-id="element-8055">
              <div className="flex items-center gap-3 mb-4" data-id="element-8056">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center text-white font-bold text-lg" data-id="element-8057">
                  Z
                </div>
                <div data-id="element-8058">
                  <div className="text-lg font-bold" data-id="element-8059">ZoomInfo</div>
                  <div className="text-xs text-gray-400" data-id="element-8060">GTM Platform</div>
                </div>
              </div>
              <p className="text-gray-400 text-sm" data-id="element-8061">
                AI-powered GTM platform for modern revenue teams
              </p>
            </div>
            <div data-id="element-8062">
              <h3 className="font-semibold mb-4" data-id="element-8063">Products</h3>
              <ul className="space-y-2 text-sm text-gray-400" data-id="element-8064">
                <li data-id="element-8065">
                  <Link to="/copilot" className="hover:text-white" data-id="element-8066">
                    Copilot
                  </Link>
                </li>
                <li data-id="element-8067">
                  <Link to="/products/gtm-studio" className="hover:text-white" data-id="element-8068">
                    GTM Studio
                  </Link>
                </li>
                <li data-id="element-8069">
                  <Link to="/orchestration" className="hover:text-white" data-id="element-8070">
                    Orchestration
                  </Link>
                </li>
                <li data-id="element-8071">
                  <Link to="/data-and-signals" className="hover:text-white" data-id="element-8072">
                    Data & Signals
                  </Link>
                </li>
                <li data-id="element-8073">
                  <Link to="/predictive" className="hover:text-white" data-id="element-8074">
                    Predictive
                  </Link>
                </li>
              </ul>
            </div>
            <div data-id="element-8075">
              <h3 className="font-semibold mb-4" data-id="element-8076">GTM Library</h3>
              <ul className="space-y-2 text-sm text-gray-400" data-id="element-8077">
                <li data-id="element-8078">
                  <Link to="/gtm/plays" className="hover:text-white" data-id="element-8079">
                    Plays
                  </Link>
                </li>
                <li data-id="element-8080">
                  <Link to="/gtm/playbooks" className="hover:text-white" data-id="element-8081">
                    Playbooks
                  </Link>
                </li>
              </ul>
            </div>
            <div data-id="element-8082">
              <h3 className="font-semibold mb-4" data-id="element-8083">Company</h3>
              <ul className="space-y-2 text-sm text-gray-400" data-id="element-8084">
                <li data-id="element-8085">
                  <Link to="/pricing" className="hover:text-white" data-id="element-8086">
                    Pricing
                  </Link>
                </li>
                <li data-id="element-8087">
                  <Link to="/website/contact" className="hover:text-white" data-id="element-8088">
                    Contact Sales
                  </Link>
                </li>
                <li data-id="element-8089">
                  <Link to="/login" className="hover:text-white" data-id="element-8090">
                    Sign In
                  </Link>
                </li>
                <li data-id="element-8091">
                  <Link to="/signup" className="hover:text-white" data-id="element-8092">
                    Get Started Free
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400" data-id="element-8093">
            © 2024 ZoomInfo GTM Platform. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}